import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, type PopoverState } from 'react-tiny-popover';
import { Button, Input } from '~/components/ui';
import { FieldError } from '~/components/ui/inputs/FieldError';
import { ProductLengthPicker } from '~/components/common/ProductLengthPicker';
import {
  useCannedTallyCandidateLengths,
  type PickableProductLengthOption,
} from '~/api/canned-tally';
import { BundleQuantityInput } from '~/components/ui/BundleQuantityInput';
import {
  bundleCountForQuantity,
  calcUnitsPerBundleForProductLength,
  hasBundleDimensions,
  unitQuantityForBundleCount,
} from '~/components/ui/bundleQuantity';
import { popoverMaxHeight } from '~/components/ui/popoverMaxHeight';
import type { UnitCode } from '~/graphql/graphql';
import { priceUnitLabel } from '~/utils';
import { MIN_POSITIVE_PRICE, MAX_PRICE } from '~/utils/moneyBounds';
import { quantityInBundles } from '~/pages/rfq-view/bundleQuantity';
import { priceLabels } from '~/pages/rfq-view/sections/quoteDisplay';
import {
  byAscendingActualLength,
  type ProductGroup,
  type ItemRow,
  type BundleDimensions,
} from '~/pages/rfq-view/itemGroups';
import type { QuoteResponseDraft, RowDraft } from './useQuoteResponseDraft';

function unitsPerBundleFor(
  bundleDimensions: BundleDimensions,
  piecesPerBundle: number,
  standardUnitsCode: UnitCode
): number {
  return calcUnitsPerBundleForProductLength(
    { value: 0, unit: standardUnitsCode },
    piecesPerBundle,
    bundleDimensions
  );
}

function ChangeQuantityPopover({
  row,
  rowDraft,
  standardUnitsCode,
  productName,
  disabled,
  onPatchRow,
  errorId,
}: Readonly<{
  row: ItemRow;
  rowDraft: RowDraft;
  standardUnitsCode: UnitCode;
  productName: string;
  disabled: boolean;
  onPatchRow: (
    platformProductLengthId: string,
    changes: Partial<RowDraft>
  ) => void;
  errorId?: string;
}>) {
  const { t } = useTranslation('rfq-view');
  const [isOpen, setIsOpen] = useState(false);
  const bundleDimensions = row.bundleDimensions!;
  const unitsPerBundle = unitsPerBundleFor(
    bundleDimensions,
    rowDraft.piecesPerBundle,
    standardUnitsCode
  );
  const bundleCount = parseInt(rowDraft.quantityInBundles, 10) || 0;
  const quantity = unitQuantityForBundleCount(
    bundleCount,
    unitsPerBundle,
    standardUnitsCode
  );

  const closeOnEscape = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') return;
    event.nativeEvent.stopPropagation();
    setIsOpen(false);
  };

  return (
    <Popover
      isOpen={isOpen}
      onClickOutside={() => setIsOpen(false)}
      positions={['bottom', 'top', 'left']}
      align="start"
      containerStyle={{ zIndex: '1100' }}
      content={(popoverState: PopoverState) => (
        <div
          data-testid={`${row.platformProductLengthId}-quantity-popover`}
          className="mt-2 w-64 overflow-y-auto rounded-xl border border-neut-border bg-neut-light p-3 shadow-xl"
          style={{ maxHeight: popoverMaxHeight(popoverState) }}
          onKeyDown={closeOnEscape}
        >
          <fieldset className="m-0 min-w-0 border-0 p-0">
            <legend className="mb-2 text-xs font-semibold text-neut-prim">
              {t('rfq_view.quantity_label')}
            </legend>
            <BundleQuantityInput
              quantity={quantity}
              platformProductsLength={bundleDimensions}
              piecesPerBundle={rowDraft.piecesPerBundle}
              disabled={disabled}
              onBundleChange={(nextQuantity) =>
                onPatchRow(row.platformProductLengthId, {
                  quantityInBundles: String(
                    bundleCountForQuantity(nextQuantity, unitsPerBundle)
                  ),
                })
              }
            />
          </fieldset>
          <button
            type="button"
            data-testid={`${row.platformProductLengthId}-quantity-done`}
            className="mt-3 w-full cursor-pointer rounded-lg bg-prim-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-prim-700"
            onClick={() => setIsOpen(false)}
          >
            {t('rfq_view.quantity_done')}
          </button>
        </div>
      )}
    >
      <div className="flex items-center gap-2">
        <span
          data-testid={`${row.platformProductLengthId}-quantity-display`}
          className="tabular-nums"
        >
          {rowDraft.quantityInBundles || '0'}
        </span>
        <button
          type="button"
          data-testid={`${row.platformProductLengthId}-quantity-change`}
          aria-label={`${t('rfq_view.quantity_change')} ${productName} ${row.displayValue}`}
          aria-expanded={isOpen}
          aria-invalid={errorId !== undefined}
          aria-describedby={errorId}
          disabled={disabled}
          className="shrink-0 cursor-pointer rounded-lg border border-neut-border bg-neut-light px-2.5 py-1.5 text-xs font-medium text-prim-700 hover:border-prim-300 hover:bg-prim-50 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => setIsOpen((wasOpen) => !wasOpen)}
        >
          {t('rfq_view.quantity_change')}
        </button>
      </div>
    </Popover>
  );
}

function fixedQuantityInBundles(row: ItemRow, rowDraft: RowDraft): string {
  if (rowDraft.quoteItemId !== null || row.requestedQuantityInPieces == null) {
    return rowDraft.quantityInBundles;
  }
  return String(
    quantityInBundles(row.requestedQuantityInPieces, row.piecesPerBundle)
  );
}

function draftedRowsInLengthOrder(
  rows: ItemRow[],
  draft: QuoteResponseDraft
): ReadonlyArray<{ row: ItemRow; rowDraft: RowDraft }> {
  return rows.sort(byAscendingActualLength).flatMap((row) => {
    const rowDraft = draft.rows.get(row.platformProductLengthId);
    return rowDraft ? [{ row, rowDraft }] : [];
  });
}

function bundleDimensionsOf(
  candidateLength: Partial<BundleDimensions>
): BundleDimensions | undefined {
  return candidateLength.length && candidateLength.platformProduct
    ? {
        isNominal: candidateLength.isNominal,
        length: candidateLength.length,
        platformProduct: candidateLength.platformProduct,
      }
    : undefined;
}

export function QuoteItemGroups({
  groups,
  draft,
  disabled,
  hasFixedLines,
  usesWholeRequestPrice,
  rowIdsMissingQuantity,
  onPatchRow,
  onAddRow,
  onRemoveRow,
}: Readonly<{
  groups: ProductGroup[];
  draft: QuoteResponseDraft;
  disabled: boolean;
  hasFixedLines: boolean;
  usesWholeRequestPrice: boolean;
  rowIdsMissingQuantity: ReadonlySet<string>;
  onPatchRow: (
    platformProductLengthId: string,
    changes: Partial<RowDraft>
  ) => void;
  onAddRow: (platformProductLengthId: string, piecesPerBundle: number) => void;
  onRemoveRow: (platformProductLengthId: string, dropEntirely: boolean) => void;
}>) {
  const { t } = useTranslation('rfq-view');
  const [addedCandidatesByProductId, setAddedCandidatesByProductId] = useState<
    Map<string, PickableProductLengthOption[]>
  >(new Map());

  const addLengthLabels = {
    label: t('rfq_view.add_length_label'),
    trigger: t('rfq_view.add_length_placeholder'),
    searchPlaceholder: t('rfq_view.add_length_search_placeholder'),
    searchLabel: t('rfq_view.add_length_search_placeholder'),
    loading: t('rfq_view.add_length_loading'),
    error: t('rfq_view.add_length_error'),
    emptyTitle: t('rfq_view.add_length_empty_title'),
    emptyMessage: t('rfq_view.add_length_empty_message'),
    noResultsTitle: t('rfq_view.add_length_no_results_title'),
    noResultsMessage: t('rfq_view.add_length_no_results_message'),
  };

  const quantityErrorFor = (platformProductLengthId: string) =>
    rowIdsMissingQuantity.has(platformProductLengthId)
      ? t('rfq_view.quantity_required_error')
      : undefined;

  const addCandidateLength = (
    group: ProductGroup,
    candidate: PickableProductLengthOption
  ) => {
    const platformProductLength = candidate.platformProductLength;
    if (!platformProductLength) return;
    setAddedCandidatesByProductId((current) => {
      const next = new Map(current);
      next.set(group.productId, [
        ...(current.get(group.productId) ?? []),
        candidate,
      ]);
      return next;
    });
    onAddRow(platformProductLength.id, group.piecesPerBundle);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-neut-sec">
            <th scope="col" className="font-normal pb-2 pr-4 whitespace-nowrap">
              {t('rfq_view.length_label')}
            </th>
            <th scope="col" className="font-normal pb-2 pr-4 whitespace-nowrap">
              {t('rfq_view.requested_label')}
            </th>
            <th scope="col" className="font-normal pb-2 pr-4 whitespace-nowrap">
              {t('rfq_view.quantity_label')}
            </th>
            {!usesWholeRequestPrice && (
              <>
                <th
                  scope="col"
                  className="font-normal pb-2 pr-4 whitespace-nowrap"
                >
                  {t('rfq_view.pickup_price_label')}
                </th>
                <th
                  scope="col"
                  className="font-normal pb-2 pr-4 whitespace-nowrap"
                >
                  {t('rfq_view.delivery_price_label')}
                </th>
              </>
            )}
            <th scope="col" className="font-normal pb-2 pr-4 whitespace-nowrap">
              <span className="sr-only">{t('rfq_view.remove_row_label')}</span>
            </th>
          </tr>
        </thead>
        {groups.map((group, groupIndex) => {
          const { pickupPriceLabel, deliveryPriceLabel } = priceLabels(
            group.standardUnitsCode,
            t
          );
          const requestedLengthIds = new Set(
            group.rows.map((row) => row.platformProductLengthId)
          );
          // A row the seller just added lives in draft.rows before the next save and
          // refetch fold it into group.rows, so it is rendered from its candidate length
          // until then.
          const addedRows: ItemRow[] = (
            addedCandidatesByProductId.get(group.productId) ?? []
          )
            .filter(
              (candidate) =>
                candidate.platformProductLength != null &&
                draft.rows.has(candidate.platformProductLength.id) &&
                !requestedLengthIds.has(candidate.platformProductLength.id)
            )
            .map((candidate) => ({
              platformProductLengthId: candidate.platformProductLength!.id,
              lengthId: candidate.length.id,
              displayValue: candidate.length.displayValue,
              actualLengthInches: candidate.length.actualLengthInches,
              piecesPerBundle: group.piecesPerBundle,
              requestedQuantityInPieces: null,
              bundleDimensions: bundleDimensionsOf(
                candidate.platformProductLength!
              ),
            }));
          const columnCount = usesWholeRequestPrice ? 4 : 6;
          const unit = group.standardUnitsCode;
          return (
            <tbody key={group.productId}>
              <tr>
                <th
                  scope="colgroup"
                  colSpan={columnCount}
                  className={`text-left font-semibold text-neut-prim pb-1 ${
                    groupIndex === 0 ? 'pt-1' : 'pt-20'
                  }`}
                >
                  {group.productName} (/
                  {priceUnitLabel(group.standardUnitsCode)})
                </th>
              </tr>
              {draftedRowsInLengthOrder(
                [...group.rows, ...addedRows],
                draft
              ).map(({ row, rowDraft }) => {
                const quantityError = quantityErrorFor(
                  row.platformProductLengthId
                );
                const quantityErrorId = `${row.platformProductLengthId}-quantity-error`;
                let quantityCell: ReactNode;
                if (hasFixedLines) {
                  quantityCell = (
                    <span
                      data-testid={`${row.platformProductLengthId}-quantity-display`}
                      className="tabular-nums"
                    >
                      {fixedQuantityInBundles(row, rowDraft)}
                    </span>
                  );
                } else if (
                  row.bundleDimensions &&
                  hasBundleDimensions(row.bundleDimensions, unit)
                ) {
                  quantityCell = (
                    <>
                      <ChangeQuantityPopover
                        row={row}
                        rowDraft={rowDraft}
                        standardUnitsCode={unit}
                        productName={group.productName}
                        disabled={disabled}
                        onPatchRow={onPatchRow}
                        errorId={quantityError ? quantityErrorId : undefined}
                      />
                      {quantityError && (
                        <FieldError id={quantityErrorId}>
                          {quantityError}
                        </FieldError>
                      )}
                    </>
                  );
                } else {
                  quantityCell = (
                    <Input
                      name={`${row.platformProductLengthId}-quantity`}
                      aria-label={`${t('rfq_view.quantity_label')} ${group.productName} ${row.displayValue}`}
                      type="number"
                      min={0}
                      step="1"
                      disabled={disabled}
                      containerClassName="min-w-[100px]"
                      error={quantityError}
                      value={rowDraft.quantityInBundles}
                      onChange={(_, value) =>
                        onPatchRow(row.platformProductLengthId, {
                          quantityInBundles: value,
                        })
                      }
                    />
                  );
                }
                return (
                  <tr key={row.platformProductLengthId}>
                    <th
                      scope="row"
                      className="text-left font-normal pr-4 py-2 text-neut-sec align-top whitespace-nowrap"
                    >
                      {row.displayValue}
                    </th>
                    <td className="pr-4 py-2 align-top text-neut-sec whitespace-nowrap">
                      {row.requestedQuantityInPieces == null
                        ? t('rfq_view.not_requested')
                        : quantityInBundles(
                            row.requestedQuantityInPieces,
                            row.piecesPerBundle
                          )}
                    </td>
                    <td className="pr-4 py-2 align-top">{quantityCell}</td>
                    {!usesWholeRequestPrice && (
                      <>
                        <td className="pr-4 py-2 align-top">
                          <Input
                            name={`${row.platformProductLengthId}-pickup-price`}
                            aria-label={`${pickupPriceLabel} ${group.productName} ${row.displayValue}`}
                            type="number"
                            min={MIN_POSITIVE_PRICE}
                            max={MAX_PRICE}
                            step="0.01"
                            disabled={disabled}
                            containerClassName="max-w-[8rem]"
                            value={rowDraft.pickupPrice}
                            onChange={(_, value) =>
                              onPatchRow(row.platformProductLengthId, {
                                pickupPrice: value,
                              })
                            }
                          />
                        </td>
                        <td className="pr-4 py-2 align-top">
                          <Input
                            name={`${row.platformProductLengthId}-delivery-price`}
                            aria-label={`${deliveryPriceLabel} ${group.productName} ${row.displayValue}`}
                            type="number"
                            min={MIN_POSITIVE_PRICE}
                            max={MAX_PRICE}
                            step="0.01"
                            disabled={disabled}
                            containerClassName="max-w-[8rem]"
                            value={rowDraft.deliveryPrice}
                            onChange={(_, value) =>
                              onPatchRow(row.platformProductLengthId, {
                                deliveryPrice: value,
                              })
                            }
                          />
                        </td>
                      </>
                    )}
                    <td className="pr-4 py-2 align-top">
                      {!hasFixedLines && (
                        <Button
                          name={`${row.platformProductLengthId}-remove`}
                          type="button"
                          variant="destructive-outline"
                          size="sm"
                          width="auto"
                          aria-label={`${t('rfq_view.remove_row_label')} ${group.productName} ${row.displayValue}`}
                          disabled={disabled}
                          onClick={() =>
                            onRemoveRow(
                              row.platformProductLengthId,
                              !requestedLengthIds.has(
                                row.platformProductLengthId
                              ) && rowDraft.quoteItemId == null
                            )
                          }
                        >
                          {t('rfq_view.remove_row_label')}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!hasFixedLines && (
                <tr>
                  <td colSpan={columnCount} className="pt-2">
                    <ProductLengthPicker
                      fetchHook={useCannedTallyCandidateLengths}
                      platformProductId={group.productId}
                      testId={`${group.productId}-add-length`}
                      labels={addLengthLabels}
                      className="w-full md:w-1/2 lg:w-1/3"
                      disabled={disabled}
                      isOfferable={(candidate) =>
                        candidate.platformProductLength != null &&
                        !draft.rows.has(candidate.platformProductLength.id)
                      }
                      onSelect={(candidate) =>
                        addCandidateLength(group, candidate)
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}
